import React, { useCallback, useEffect, useRef } from 'react'
import { Hook } from '../hooks/Hook'
import { useSingletonMVUReducer } from '../mvuReducer'
import { formatDurationSeconds, secondsUntil } from '../utils/time'
import {
  CountdownState,
  Dispatch,
  RecordingPausedState,
  RecordingState,
  WebRecorder2State,
  recordingCountdownSeconds,
  WebRecorder2Action,
  WebRecorder2SideEffect,
  subjectId,
  SetSubjectId,
} from './webRecorder2'
import { AudioInputSelector } from './microphoneInput/react'
import { recordingDurationSeconds } from './recorder'
import { TrashIcon } from '@heroicons/react/24/outline'
import { IconButton } from '~/components/IconButton'
import { Modal } from '~/components/Modal'
import { take } from '~/utils/map'
import { Loading } from '~/components/Loading'
import * as PubSub from './pubSub'
import { DownloadButton, PauseButton, PlayingDisclosureView, StartRecordingIcon, UploadProgress } from './shared'
import { TickUpdated } from './shared'
import { Button } from '~/components/Button'

export function WebRecorder2MeetingDetail({ hook }: { hook: Hook }) {
  const canUseWebRecorder = hook.el.hasAttribute('data-can-use-web-recorder')
  const canDownload = hook.el.hasAttribute('data-can-download')
  const byoProcessingMessage = hook.el.dataset.byoProcessingMessage

  const processingError = hook.el.dataset.processingError
  // TODO: Move processing state into webRecorder2 state
  const isProcessing = hook.el.hasAttribute('data-is-processing')
  const otherRemoteClientIsRecording = hook.el.hasAttribute('data-other-client-is-recording')

  const detailSubjectId = canUseWebRecorder
    ? (hook.el.dataset.meetingId ?? hook.el.dataset.integrationReferenceId)
    : undefined

  const [state, dispatch] = useSingletonMVUReducer<WebRecorder2State, WebRecorder2Action, WebRecorder2SideEffect>(
    'webRecorder2'
  )

  useEffect(() => {
    // If we're already recording, we don't want to stop the prior recording.
    const alreadyRecording = state && ['countdown', 'playing-disclosure', 'recording'].includes(state.mode)

    if (canUseWebRecorder && detailSubjectId && !alreadyRecording) {
      const action: SetSubjectId = {
        action: 'set-subject-id',
        subjectId: detailSubjectId,
        startRecording: window.location.search.includes('capture_now=true') ? 'ifNoPreviousRecordingExists' : 'no',
      }

      PubSub.publish('apply-action', action)
      PubSub.publish('is-recordable', { subjectId: detailSubjectId })
    }
  }, [detailSubjectId])

  useEffect(() => {
    if (detailSubjectId && processingError) {
      PubSub.publish('apply-action', {
        action: 'handle-error-message',
        message: processingError,
        detailSubjectId,
      })
    }
  }, [detailSubjectId, processingError])

  const currentSubject = subjectId(state)
  const recordingAnotherMeeting = currentSubject && currentSubject !== detailSubjectId

  const renderCount = useRef(0)
  const hadAChanceToSetSubjectId = renderCount.current > 1

  useEffect(() => {
    if (recordingAnotherMeeting && hadAChanceToSetSubjectId) {
      debugLog('Capturing another meeting:', currentSubject, 'vs.', detailSubjectId)
    }
  }, [recordingAnotherMeeting, hadAChanceToSetSubjectId])

  renderCount.current += 1

  if (!state) return <Loading size="sm" />
  if (!canUseWebRecorder) return null

  const showInputSelector = ['recording', 'paused', 'countdown'].includes(state.mode) && !state.errorMessage
  const showSubmitButton = !isProcessing && state.mode === 'paused'

  return (
    <div className="flex flex-row gap-2 items-center">
      {byoProcessingMessage ? (
        <Loading size="sm">{byoProcessingMessage}</Loading>
      ) : recordingAnotherMeeting ? (
        <Loading size="sm">You’re capturing another meeting&hellip;</Loading>
      ) : state.errorMessage ? (
        <div className="text-xs text-red-500 text-center pt-2">{state.errorMessage || processingError}</div>
      ) : isProcessing && state.mode !== 'complete' ? (
        <Loading size="sm">Processing audio&hellip;</Loading>
      ) : state.otherLocalClientIsRecording && state.mode !== 'recording' ? (
        <Loading size="sm">You’re capturing a meeting in another tab&hellip;</Loading>
      ) : otherRemoteClientIsRecording && state.mode !== 'recording' ? (
        <Loading size="sm">Another browser is capturing this meeting&hellip;</Loading>
      ) : (
        <>
          {showSubmitButton && (
            <Button
              onClick={() => dispatch({ action: 'submit-recording' })}
              disabled={!state.isOnline}
              variant="primary"
              size="md"
              tooltip={
                state.isOnline
                  ? 'Upload the captured meeting now'
                  : 'Your computer does not appear to be connected to the internet.'
              }
            >
              Submit
            </Button>
          )}
          {state.microphoneInputState && showInputSelector && (
            <AudioInputSelector
              state={state.microphoneInputState}
              dispatch={dispatch}
              variant="icon-only"
              align="end"
            />
          )}
          <MeetingDetailBody state={state} dispatch={dispatch} canDownload={canDownload} />
        </>
      )}
    </div>
  )
}

function MeetingDetailBody({
  state,
  canDownload,
  dispatch,
}: {
  state: WebRecorder2State
  canDownload: boolean
  dispatch: Dispatch
}): React.ReactElement {
  switch (state.mode) {
    case 'initial':
      return <Loading size="sm" />
    case 'initialized':
      return state.initializingStream ? <Loading size="sm" /> : <InitialView dispatch={dispatch} />
    case 'playing-disclosure':
      return <PlayingDisclosureView />
    case 'recording':
      return <RecordingView state={state} dispatch={dispatch} />
    case 'paused':
      return <RecordingPausedView state={state} dispatch={dispatch} canDownload={canDownload} />
    case 'countdown':
      return <RecordingCountdownView state={state} />
    case 'uploading':
      return (
        <>
          {canDownload && <DownloadButton onClick={() => dispatch({ action: 'download-recording' })} />}
          <UploadProgress progressPercent={state.progressPercent} />
        </>
      )
    case 'complete':
      return (
        <div className="flex flex-row gap-2 items-center">
          {canDownload && <DownloadButton onClick={() => dispatch({ action: 'download-recording' })} />}
          Processing&hellip;
        </div>
      )
  }
}

function InitialView({ dispatch }: { dispatch: Dispatch }) {
  return (
    <IconButton
      icon={<StartRecordingIcon size="sm" />}
      screenReaderText="Capture meeting now"
      tooltip="Capture meeting now"
      variant="outline"
      onClick={() => dispatch({ action: 'start-new-recording' })}
    />
  )
}

function RecordingView({ state, dispatch }: { state: RecordingState; dispatch: Dispatch }) {
  const currentDuration = useCallback(
    (now: number) => {
      const times = take(state.recorder, ['prevDurationMs', 'startTimeMs'])
      const durationSeconds = recordingDurationSeconds(times, now)
      return <span className="tabular-nums inline-block">{formatDurationSeconds(durationSeconds)}</span>
    },
    [state.recorder.prevDurationMs, state.recorder.startTimeMs]
  )

  return (
    <div className="flex flex-row gap-2 items-center">
      <PauseButton onClick={() => dispatch({ action: 'pause-recording' })} size="sm" />
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          Capturing
          <TickUpdated currentValue={currentDuration} />
        </div>
      </div>
    </div>
  )
}

export function durationDisplayMinWidthClass(durationSeconds: number) {
  const wholeMinutes = Math.floor(durationSeconds / 60)

  if (wholeMinutes < 10) {
    return 'min-w-8'
  } else if (wholeMinutes < 60) {
    return 'min-w-10'
  } else if (wholeMinutes < 120) {
    return 'min-w-12'
  } else {
    return 'min-w-14'
  }
}

function RecordingPausedView({
  state,
  canDownload,
  dispatch,
}: {
  state: RecordingPausedState
  canDownload: boolean
  dispatch: Dispatch
}) {
  return (
    <div className="flex flex-row gap-2 items-center">
      <Modal
        title="Delete captured meeting?"
        trigger={
          <IconButton
            icon={<TrashIcon />}
            screenReaderText="Delete captured meeting"
            tooltip="Delete captured meeting"
          />
        }
        confirm={
          <button
            className="pc-button pc-button--danger pc-button--sm bg-danger-inverse py-2"
            onClick={() => dispatch({ action: 'delete-recording' })}
          >
            Delete
          </button>
        }
      >
        <p className="max-w-[26rem]">Are you sure you want to delete this capture? This action cannot be undone.</p>
      </Modal>
      {canDownload && <DownloadButton onClick={() => dispatch({ action: 'download-recording' })} />}
      <ResumeButton onClick={() => dispatch({ action: 'resume-recording' })} disabled={state.resuming} />
      Paused {formatDurationSeconds(recordingDurationSeconds(state.recorder))}
    </div>
  )
}

function ResumeButton({ onClick, disabled }: { onClick: () => void; disabled: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        w-5 h-5 relative group
        hover:scale-[130%] transition-transform duration-100
      `}
    >
      <span className="sr-only">Resume capture</span>
      <div className="w-[18px] h-[18px] left-px top-px absolute bg-primary rounded-full border border-secondary" />
      <div
        className={`
          w-3 h-3 left-1 top-1 absolute bg-danger-icon rounded-2xl
          group-active:w-[16px] group-active:h-[16px] group-active:left-[2px] group-active:top-[2px] transition-all duration-100
        `}
      />
    </button>
  )
}

function RecordingCountdownView({ state: { recordingStartTimeMs, stream } }: { state: CountdownState }) {
  return (
    <div className="flex flex-row gap-2 items-center text-primary text-sm">
      Capture starting in{' '}
      <span className="min-w-4 inline-block">
        {recordingStartTimeMs && stream ? (
          <TickUpdated currentValue={(now) => Math.max(0, secondsUntil(recordingStartTimeMs, now))} />
        ) : (
          recordingCountdownSeconds
        )}
      </span>
    </div>
  )
}

function debugLog(...args: any[]) {
  /* eslint-disable-next-line no-console */
  console.log('[MeetingDetail]', ...args)
}
